import { snakeCase } from 'lodash';
import { pipe } from 'ramda';
import React, { FC, ReactNode } from 'react';
import { FieldOptionBase } from 'api/tasks';
import { SelectMode } from 'components/atoms/select';
import { getControlledSelectBasedEditor } from 'components/organisms/field-editors/components/controlled-select-based-editor';
import { TriggerContent } from 'components/organisms/field-editors/components/controlled-select-based-editor-trigger-content';
import { flatMap, map, ofNullable, orElse } from 'core/maybe';
import { TranslationKey, useTranslation } from 'hooks/use-translation';
import { SingleSelectField, SingleUserField } from '../fields';
import { SelectFilterOperationType } from '../filters/select';
import { EditorComponents } from './editor-components';

interface Props {
  field: SingleSelectField | SingleUserField;
  filterType:
    | SelectFilterOperationType.Is
    | SelectFilterOperationType.IsNot
    | SelectFilterOperationType.IsAtLeast
    | SelectFilterOperationType.IsAtMost;
  value: number | null;
  options: FieldOptionBase[];
  components: EditorComponents;
  type: SelectMode;
  onChange: (next: number | null) => void;
}

const Editor = getControlledSelectBasedEditor<false, number>(
  'single-select-editor',
  'editors.single-select.select',
);

export const SingleSelectEditor: FC<Props> = ({
  field,
  filterType,
  value,
  components: { TriggerOption, ...selectComponents },
  options,
  onChange,
  type,
}) => {
  const title = useTranslation('task_list.filters.messages.editor_title', {
    field: field.fieldLabel,
    operation: useTranslation(
      `task_list.filters.${snakeCase(filterType)}` as TranslationKey,
    ),
  });
  const items = pipe(
    () => value,
    ofNullable,
    map((x) => options.find((o) => o.value === x) ?? null),
    flatMap(ofNullable),
    map((x) => [<TriggerOption option={x} />]),
    orElse<ReactNode[]>([]),
  )();

  return (
    <Editor
      title={title}
      components={selectComponents}
      isMulti={false}
      options={options}
      value={options.find((u) => u.value === value)}
      onChange={(selected?: FieldOptionBase<number>) =>
        onChange(selected?.value ?? null)
      }
      triggerContent={<TriggerContent items={items} />}
      type={type}
    />
  );
};
